// FORMA UNUI ESV — T, L, +, U, H și restul, crescute din zidurile arhitecturii.
//
// Schema serverului (`geometrie/tipuri.py`) descrie un ESV ca PICIOR (axa lui,
// `L_cm` × `b_cm`, alunecat cu `ofs_mm`) plus BRAȚE pe axa cealaltă:
// `{L_cm, b_cm, ofs_mm, la_mm}` — `la_mm` e locul brațului de-a lungul
// piciorului, față de nod, `ofs_mm` alunecarea lui pe axa lui. Forma iese din
// alunecări: braț la capătul piciorului, de o parte — L; la capăt, de amândouă
// părțile, sau la mijloc, de o parte — T; la mijloc, de amândouă — +; două
// brațe la capete — U, H sau Z. Pasul 4 dă FEM-ului inerțiile poligonului
// întreg. Un braț din braț nu are loc în schemă, deci nici aici.
//
// Aici stă tot ce se poate socoti fără ecran: unde poate CONTINUA elementul
// (`continuari` — semnele «+» de pe desen), ce face fiecare continuare
// (`aplicaContinuarea`), brațele din panou (`redimBrat`, `stergeBrat`) și
// numele formei (`numeleFormei`). Capetele libere ale pieselor nu au «+»: ele
// se trag cu mâna (`editorCapete`). Coordonatele sunt milimetrii planșei, în
// cadrul clădirii, unde zidurile stau pe X și Y.
//
// REGULA VERTICALĂ. O secțiune poate scădea în sus, niciodată crește (nici prin
// brațe — `tipuri.valideaza`). Deci o creștere la un nivel se face și pe toate
// nivelurile de DEDESUBT, iar o scădere și pe cele de DEASUPRA.

import {
  amprentaEsv, axelePeretilorMm, centrulEsv, LATURA_MIN_CM, sectiunea,
  type AxaPerete, type Brat, type Esv, type Geometrie, type NivelContext, type Pt,
  type SectiuneEsv,
} from "./editorGeometrie";
import { piesele } from "./editorCapete";

/** Cel mai scurt pas pe care îl propune un «+»: sub atât zidul nu mai
 *  continuă, e doar grosimea tencuielii sau o rotunjire. */
export const CONTINUARE_MIN_MM = 50;
/** Cât de departe se uită o continuare, de-a lungul zidului. */
const RAZA_MM = 12_000;
/** Pasul cu care se merge pe zid. */
const PAS_MM = 10;

/** Un «+» de pe desen: unde stă, încotro crește elementul și cu cât.
 *
 *  * `picior` — piciorul se lungește la capătul `capat` (±1 pe axa lui), un
 *    COLȚ: capătul pe care stă un braț (cel liber se trage);
 *  * `brat_nou` — un braț nou, la `la_mm` de nod, spre `parte` (±1 pe axa
 *    cealaltă), `b_mm` gros — grosimea zidului în care intră;
 *  * `brat` — brațul `index` se lungește spre `parte`.
 *
 *  `lung_mm` e cât crește: până unde continuă zidul, cel mult până la
 *  următoarea îmbinare cu un zid de pe cealaltă direcție (acolo apare
 *  următorul «+», ca omul să poată întoarce), un gol sau alt ESV. */
export type Continuare =
  | { fel: "picior"; capat: 1 | -1; lung_mm: number; poz: Pt; dir: Pt }
  | { fel: "brat_nou"; la_mm: number; parte: 1 | -1; b_mm: number; lung_mm: number;
      poz: Pt; dir: Pt }
  | { fel: "brat"; index: number; parte: 1 | -1; lung_mm: number; poz: Pt; dir: Pt };

/** Indicii axelor: `lg` de-a lungul piciorului, `tr` de-a curmezișul. */
const indici = (e: Esv): { lg: 0 | 1; tr: 0 | 1 } =>
  (e.axa === "X" ? { lg: 0, tr: 1 } : { lg: 1, tr: 0 });

type Cutie = [number, number, number, number];
const cutia = (pts: Pt[]): Cutie => {
  const xs = pts.map((q) => q[0]);
  const ys = pts.map((q) => q[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
};
const inCutie = (p: Pt, c: Cutie, m = 0) =>
  p[0] >= c[0] - m && p[0] <= c[2] + m && p[1] >= c[1] - m && p[1] <= c[3] + m;

/** Ce vede o continuare pe nivel: zidurile (ca axe, pe X și Y), golurile și
 *  betonul celorlalte ESV-uri. */
type Scena = { axe: AxaPerete[]; goluri: Cutie[]; altele: Cutie[] };

function scena(G: Geometrie, e: Esv, nivel: number, n: NivelContext): Scena {
  return {
    axe: axelePeretilorMm(n),
    goluri: n.goluri.filter((g) => g.colturi.length >= 3).map((g) => cutia(g.colturi)),
    altele: G.esv.filter((x) => x.id !== e.id)
      .flatMap((x) => amprentaEsv(x, nivel).map(cutia)),
  };
}

/** Punctul `p` stă în beton de zid: în bandă unui perete, nu într-un gol. */
const inZid = (s: Scena, p: Pt) =>
  s.axe.some((a) => {
    const [lung, trans] = a.axa === "X" ? [p[0], p[1]] : [p[1], p[0]];
    return lung >= a.de - 0.2 && lung <= a.la + 0.2
      && Math.abs(trans - a.cota) <= a.grosime / 2 + 0.5;
  }) && !s.goluri.some((g) => inCutie(p, g, -0.5));

/** Cât se poate merge din `p`, pe direcția `d` (versor pe X sau Y), prin zid:
 *  până unde zidul se termină, dă într-un gol sau în alt ESV — și cel mult
 *  până după prima ÎMBINARE cu un zid de pe cealaltă direcție (elementul o
 *  cuprinde întreagă; de acolo se poate întoarce). */
function drum(s: Scena, p: Pt, d: Pt): number {
  const dirAxa = d[0] !== 0 ? "X" : "Y";
  // zidurile de pe cealaltă direcție care taie drumul, cu banda lor pe drum
  const k: 0 | 1 = d[0] !== 0 ? 0 : 1;
  const semn = d[k];
  const peste = s.axe.filter((a) => a.axa !== dirAxa).map((a) => {
    // capătul de lângă și cel de departe al benzii, măsurate de la `p`
    const t0 = (a.cota - a.grosime / 2 - p[k]) * semn;
    const t1 = (a.cota + a.grosime / 2 - p[k]) * semn;
    const q = k === 0 ? p[1] : p[0];
    const trece = q >= a.de - 0.5 && q <= a.la + 0.5;
    return trece ? [Math.min(t0, t1), Math.max(t0, t1)] : null;
  }).filter((x): x is number[] => !!x && x[0] > PAS_MM);
  const liber = (u: number) => {
    const q: Pt = [p[0] + d[0] * u, p[1] + d[1] * u];
    return inZid(s, q) && !s.altele.some((c) => inCutie(q, c, -0.5));
  };
  let t = 0;
  while (t < RAZA_MM) {
    if (!liber(t + PAS_MM - 0.5)) {
      // ultimul pas, pe milimetru: zidul se termină unde se termină, nu pe pas
      while (t < RAZA_MM && liber(t + 1)) t += 1;
      return t;
    }
    t += PAS_MM;
    const imb = peste.find(([a, b]) => a < t && b <= t + 1e-6);
    if (imb) return imb[1];
  }
  return t;
}

const rot = (v: number) => Math.round(v);

/** Grosimea unui braț care intră într-un zid: a zidului, pe 5 cm, nu sub
 *  latura minimă. */
const grosimeaBratului = (g_mm: number) =>
  Math.max(LATURA_MIN_CM * 10, Math.round(g_mm / 50) * 50);

/** Brațul, ca interval pe cele două axe ale piciorului, față de nod. */
function bratulIn(b: Brat) {
  return { la0: b.la_mm - b.b_cm * 5, la1: b.la_mm + b.b_cm * 5,
           t0: b.ofs_mm - b.L_cm * 5, t1: b.ofs_mm + b.L_cm * 5 };
}

/** UNDE POATE CONTINUA ESV-ul `e` la `nivel`: semnele «+» din jurul lui.
 *
 *  Capetele libere — al piciorului fără braț lipit, vârfurile brațelor — se
 *  trag cu mâna, deci n-au «+». Rămân colțurile piciorului (de acolo piciorul
 *  trece peste braț: T-ul refăcut din L) și brațele noi, unde un zid de pe
 *  cealaltă direcție atinge piciorul (și brațul scos, refăcut). O amprentă
 *  purtată din Pasul 4 nu are piese de tras: acolo «+» lungește și capetele,
 *  și brațele. */
export function continuari(G: Geometrie, e: Esv, nivel: number,
                           n: NivelContext | null): Continuare[] {
  const sec = sectiunea(e, nivel);
  if (!sec || !n) return [];
  const s = scena(G, e, nivel, n);
  const { lg, tr } = indici(e);
  const ps = piesele(e, nivel);
  const c = centrulEsv(e, nivel);
  const L = sec.L_cm * 10;
  const b = sec.b_cm * 10;
  // piciorul cu colțurile lui: betonul brațelor de la capăt e al lui
  const [a0, a1] = ps ? ps.picior : [c[lg] - L / 2, c[lg] + L / 2];
  const p0 = e.nod[tr];
  const pt = (a: number, t: number): Pt => (lg === 0 ? [a, t] : [t, a]);
  const vLg = (sgn: number): Pt => (lg === 0 ? [sgn, 0] : [0, sgn]);
  const vTr = (sgn: number): Pt => (tr === 0 ? [sgn, 0] : [0, sgn]);
  const brate = sec.brate ?? [];
  const out: Continuare[] = [];

  // 1. piciorul, la colțuri
  for (const capat of ps ? ps.colturi : [-1, 1] as const) {
    const fata = capat > 0 ? a1 : a0;
    const lung = drum(s, pt(fata, p0), vLg(capat));
    if (lung >= CONTINUARE_MIN_MM)
      out.push({ fel: "picior", capat, lung_mm: rot(lung), poz: pt(fata, p0), dir: vLg(capat) });
  }

  // 2. brațele care există, la vârfurile lor libere (numai pe o amprentă
  //    purtată: altfel vârful se trage)
  if (!ps) brate.forEach((br, index) => {
    const x = bratulIn(br);
    const la = e.nod[lg] + br.la_mm;
    for (const parte of [-1, 1] as const) {
      const varf = p0 + (parte > 0 ? x.t1 : x.t0);
      if ((varf - p0) * parte <= b / 2 + 1) continue;       // nu iese din picior pe partea asta
      const lung = drum(s, pt(la, varf), vTr(parte));
      if (lung >= CONTINUARE_MIN_MM)
        out.push({ fel: "brat", index, parte, lung_mm: rot(lung), poz: pt(la, varf), dir: vTr(parte) });
    }
  });

  // 3. brațe noi: unde un zid de pe cealaltă direcție atinge piciorul
  const axaBrat = e.axa === "X" ? "Y" : "X";
  const vazute = new Set<string>();
  for (const w of s.axe) {
    if (w.axa !== axaBrat) continue;
    const bb = grosimeaBratului(w.grosime);
    // banda zidului trebuie să cadă pe picior; brațul stă pe AXA zidului,
    // chiar dacă iese puțin din capătul piciorului — atunci piciorul se
    // lungește până la fața brațului (`aplicaContinuarea`), ca un L adevărat
    if (w.cota + w.grosime / 2 < a0 + 1 || w.cota - w.grosime / 2 > a1 - 1) continue;
    const la = w.cota;
    for (const parte of [-1, 1] as const) {
      const fata = p0 + parte * b / 2;
      // zidul pleacă din fața piciorului, pe partea asta
      const [de, pana] = [w.de, w.la];
      const ajunge = parte > 0 ? de <= fata + 20 && pana > fata + CONTINUARE_MIN_MM
                               : pana >= fata - 20 && de < fata - CONTINUARE_MIN_MM;
      if (!ajunge) continue;
      // un braț care iese deja pe partea asta, acolo, se lungește din vârf (2.)
      const are = brate.some((br) => {
        const x = bratulIn(br);
        const peLoc = Math.abs(e.nod[lg] + br.la_mm - la) < bb / 2;
        return peLoc && (parte > 0 ? p0 + x.t1 > fata + 1 : p0 + x.t0 < fata - 1);
      });
      if (are) continue;
      const cheie = `${Math.round(la)}:${parte}`;
      if (vazute.has(cheie)) continue;
      vazute.add(cheie);
      const lung = drum(s, pt(la, fata), vTr(parte));
      if (lung >= CONTINUARE_MIN_MM)
        out.push({ fel: "brat_nou", la_mm: rot(la - e.nod[lg]), parte, b_mm: bb,
                   lung_mm: rot(lung), poz: pt(la, fata), dir: vTr(parte) });
    }
  }
  return out;
}

/** Ce beton ar adăuga o continuare: dreptunghiul, în mm, de hașurat la
 *  trecerea peste «+». */
export function betonulContinuarii(e: Esv, nivel: number, c: Continuare): Pt[] {
  const sec = sectiunea(e, nivel);
  if (!sec) return [];
  const { lg } = indici(e);
  const lat = c.fel === "picior" ? sec.b_cm * 10
    : c.fel === "brat_nou" ? c.b_mm : (sec.brate ?? [])[c.index]?.b_cm * 10 || 0;
  const q0 = c.poz;
  const q1: Pt = [q0[0] + c.dir[0] * c.lung_mm, q0[1] + c.dir[1] * c.lung_mm];
  // lățimea stă de-a curmezișul direcției de creștere
  const peX = c.dir[0] !== 0;
  void lg;
  const [x0, x1] = [Math.min(q0[0], q1[0]), Math.max(q0[0], q1[0])];
  const [y0, y1] = [Math.min(q0[1], q1[1]), Math.max(q0[1], q1[1])];
  return peX
    ? [[x0, q0[1] - lat / 2], [x1, q0[1] - lat / 2], [x1, q0[1] + lat / 2], [x0, q0[1] + lat / 2]]
    : [[q0[0] - lat / 2, y0], [q0[0] + lat / 2, y0], [q0[0] + lat / 2, y1], [q0[0] - lat / 2, y1]];
}

/** Secțiunile de schimbat: la `nivel` și sub el (creștere) sau deasupra
 *  (scădere); pe toate, cu `nivel == null`. Amprenta purtată din Pasul 4
 *  descria vechea formă, deci pleacă. */
function peNiveluri(G: Geometrie, id: string, nivel: number | null, creste: boolean,
                    f: (s: SectiuneEsv, n: number) => SectiuneEsv): Geometrie {
  return {
    ...G,
    esv: G.esv.map((e) => {
      if (e.id !== id) return e;
      const sec: Record<string, SectiuneEsv> = {};
      for (const [k, s] of Object.entries(e.sectiune_per_nivel)) {
        const n = Number(k);
        const atins = nivel == null || (creste ? n <= nivel : n >= nivel);
        if (!atins) { sec[k] = s; continue; }
        const { dim_extra: _x, ...rest } = f(s, n);
        void _x;
        sec[k] = rest;
      }
      return { ...e, sectiune_per_nivel: sec };
    }),
  };
}

/** Face ce promite «+»-ul `c` pe ESV-ul `id`, la `nivel` (și sub el) sau pe
 *  toate nivelurile (`nivel == null`). Pe un nivel de dedesubt forma poate fi
 *  alta: acolo aceeași creștere se aplică pe ce e, doar dacă încape logic —
 *  un braț care există deja la locul lui se lungește cel puțin cât sus. */
export function aplicaContinuarea(G: Geometrie, id: string, nivelRef: number,
                                  nivel: number | null, c: Continuare): Geometrie {
  const e = G.esv.find((x) => x.id === id);
  const ref = e && sectiunea(e, nivelRef);
  if (!e || !ref) return G;
  const r1 = (v: number) => Math.round(v * 10) / 10;
  if (c.fel === "picior") {
    // de la fața de pe desen — cu colțul brațului, dacă e unul acolo; capătul
    // celălalt rămâne pe loc: la fiecare nivel, piciorul de dedesubt cuprinde
    // cel puțin capetele celui de sus
    const { lg } = indici(e);
    const o = ref.ofs_mm ?? e.ofs_mm ?? 0;
    const [P0, P1] = piesele(e, nivelRef)?.picior
      ?? [e.nod[lg] + o - ref.L_cm * 5, e.nod[lg] + o + ref.L_cm * 5];
    const [s0, s1] = c.capat > 0 ? [P0 - e.nod[lg], P1 + c.lung_mm - e.nod[lg]]
      : [P0 - c.lung_mm - e.nod[lg], P1 - e.nod[lg]];
    return peNiveluri(G, id, nivel, true, (s) => {
      const o = s.ofs_mm ?? e.ofs_mm ?? 0;
      const lo = Math.min(o - s.L_cm * 5, s0);
      const hi = Math.max(o + s.L_cm * 5, s1);
      return { ...s, L_cm: r1((hi - lo) / 10), ofs_mm: r1((lo + hi) / 2) };
    });
  }
  if (c.fel === "brat") {
    const br = (ref.brate ?? [])[c.index];
    if (!br) return G;
    const L1 = br.L_cm + c.lung_mm / 10;
    const o1 = br.ofs_mm + c.parte * c.lung_mm / 2;
    const crescut: Brat = { ...br, L_cm: r1(L1), ofs_mm: r1(o1) };
    return peNiveluri(G, id, nivel, true, (s) => {
      const lista = s.brate ?? [];
      // pe un nivel de dedesubt fără brațul ăsta, el vine întreg: altfel sus
      // ar fi mai mult beton decât jos
      if (!lista.some((x) => Math.abs(x.la_mm - br.la_mm) <= 1))
        return { ...s, brate: [...lista, crescut] };
      return { ...s, brate: lista.map((x) => {
        if (Math.abs(x.la_mm - br.la_mm) > 1) return x;
        const lo = Math.min(x.ofs_mm - x.L_cm * 5, o1 - L1 * 5);
        const hi = Math.max(x.ofs_mm + x.L_cm * 5, o1 + L1 * 5);
        return { ...x, b_cm: Math.max(x.b_cm, br.b_cm), L_cm: r1((hi - lo) / 10),
                 ofs_mm: r1((lo + hi) / 2) };
      }) };
    });
  }
  // un braț nou: din fața de departe a piciorului (acoperă grosimea lui) până
  // unde merge zidul
  const b = ref.b_cm * 10;
  const t0 = -c.parte * b / 2;
  const t1 = c.parte * (b / 2 + c.lung_mm);
  const nou: Brat = { la_mm: c.la_mm, b_cm: r1(c.b_mm / 10),
                      L_cm: r1(Math.abs(t1 - t0) / 10), ofs_mm: r1((t0 + t1) / 2) };
  // piciorul cuprinde brațul pe lungimea lui
  const [n0, n1] = [nou.la_mm - nou.b_cm * 5, nou.la_mm + nou.b_cm * 5];
  return peNiveluri(G, id, nivel, true, (s0) => {
    const o = s0.ofs_mm ?? e.ofs_mm ?? 0;
    const lo = Math.min(o - s0.L_cm * 5, n0);
    const hi = Math.max(o + s0.L_cm * 5, n1);
    const s = lo < o - s0.L_cm * 5 - 0.05 || hi > o + s0.L_cm * 5 + 0.05
      ? { ...s0, L_cm: r1((hi - lo) / 10), ofs_mm: r1((lo + hi) / 2) } : s0;
    const lista = s.brate ?? [];
    const acolo = lista.findIndex((x) => Math.abs(x.la_mm - nou.la_mm) < nou.b_cm * 5);
    if (acolo < 0) return { ...s, brate: [...lista, nou] };
    // brațul de pe partea cealaltă, la același loc, trece prin picior: cruce
    return { ...s, brate: lista.map((x, i) => {
      if (i !== acolo) return x;
      const lo = Math.min(x.ofs_mm - x.L_cm * 5, nou.ofs_mm - nou.L_cm * 5);
      const hi = Math.max(x.ofs_mm + x.L_cm * 5, nou.ofs_mm + nou.L_cm * 5);
      return { ...x, b_cm: Math.max(x.b_cm, nou.b_cm), L_cm: r1((hi - lo) / 10),
               ofs_mm: r1((lo + hi) / 2) };
    }) };
  });
}

/** Brațul de la `la_mm` al ESV-ului, cu mărimile noi: `L_cm` crește sau
 *  scade dinspre vârful care iese din picior (capătul de la picior rămâne),
 *  `b_cm` în jurul locului lui. Creșterea merge în jos, scăderea în sus. */
export function redimBrat(G: Geometrie, id: string, nivelRef: number, nivel: number | null,
                          index: number, m: { L_cm?: number; b_cm?: number }): Geometrie {
  const e = G.esv.find((x) => x.id === id);
  const ref = e && sectiunea(e, nivelRef);
  const br = ref?.brate?.[index];
  if (!e || !ref || !br) return G;
  const b = ref.b_cm * 10;
  // vârful care iese din picior: partea spre care e alunecat brațul
  const parte = br.ofs_mm >= 0 ? 1 : -1;
  const fix = br.ofs_mm - parte * br.L_cm * 5;          // capătul de la picior
  const L = Math.max(m.L_cm ?? br.L_cm, b / 10 + LATURA_MIN_CM);
  const bb = Math.max(LATURA_MIN_CM, m.b_cm ?? br.b_cm);
  const creste = L > br.L_cm + 1e-6 || bb > br.b_cm + 1e-6;
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const dorit: Brat = { ...br, L_cm: r1(L), b_cm: r1(bb), ofs_mm: r1(fix + parte * L * 5) };
  return peNiveluri(G, id, nivel, creste, (s, n) => {
    const lista = s.brate ?? [];
    const i = lista.findIndex((x) => Math.abs(x.la_mm - br.la_mm) <= 1);
    if (i < 0) return creste ? { ...s, brate: [...lista, dorit] } : s;
    const x = lista[i];
    // la nivelul privit, exact cât s-a scris; dedesubt cel puțin atât, deasupra
    // cel mult — ca să nu crească nimic în sus
    const ia = (a: number, v: number) => (n === nivelRef ? v : creste ? Math.max(a, v) : Math.min(a, v));
    const Ln = ia(x.L_cm, dorit.L_cm);
    const bn = ia(x.b_cm, dorit.b_cm);
    const nou = { ...x, L_cm: r1(Ln), b_cm: r1(bn), ofs_mm: r1(fix + parte * Ln * 5) };
    return { ...s, brate: lista.map((y, j) => (j === i ? nou : y)) };
  });
}

/** Scoate brațul de la locul lui — de la `nivel` în sus, sau de tot. */
export function stergeBrat(G: Geometrie, id: string, nivelRef: number, nivel: number | null,
                           index: number): Geometrie {
  const e = G.esv.find((x) => x.id === id);
  const br = e && sectiunea(e, nivelRef)?.brate?.[index];
  if (!e || !br) return G;
  return peNiveluri(G, id, nivel, false, (s) => {
    const brate = (s.brate ?? []).filter((x) => Math.abs(x.la_mm - br.la_mm) > 1);
    const { brate: _b, ...rest } = s;
    void _b;
    return brate.length ? { ...rest, brate } : rest;
  });
}

/** Numele formei de la `nivel`: I, L, T, +, sau «compusă» cu mai multe
 *  brațe — ce vede omul în panou. */
export function numeleFormei(e: Esv, nivel: number): "I" | "L" | "T" | "+" | "compusa" {
  const sec = sectiunea(e, nivel);
  const brate = sec?.brate ?? [];
  if (!sec || !brate.length) return "I";
  if (brate.length > 1) return "compusa";
  const br = brate[0];
  const b = sec.b_cm * 10;
  const o = sec.ofs_mm ?? e.ofs_mm ?? 0;
  const a0 = o - sec.L_cm * 5, a1 = o + sec.L_cm * 5;
  const laCapat = br.la_mm - br.b_cm * 5 <= a0 + 1 || br.la_mm + br.b_cm * 5 >= a1 - 1;
  const x = bratulIn(br);
  const deAmbele = x.t0 < -b / 2 - 1 && x.t1 > b / 2 + 1;
  if (laCapat) return deAmbele ? "T" : "L";
  return deAmbele ? "+" : "T";
}

